import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';

import { cn } from '../../shared/lib/cn';
import { clockTime } from '../../shared/lib/format';
import { Button } from '../../shared/ui/button';
import { Input } from '../../shared/ui/input';
import { PlusIcon, SearchIcon } from '../../shared/ui/icons';
import { ErrorState, LoadingState } from '../../shared/ui/states';
import { listUsedIn, usedInKeys, type TUsedInKind, type TUsedInRow } from '../../entities/used-in';
import { FlowCreate } from '../flow-create';
import { UsedInRow } from './ui/used-in-row';

const SHOWN = 5;

const EMPTY: Record<TUsedInKind, { description: string; action: string }> = {
  source: {
    description: 'No flow starts from it, so no flow runs on its requests.',
    action: 'New flow from this source',
  },
  destination: {
    description: 'No flow delivers here, so nothing is sent to it.',
    action: 'Use it in a new flow',
  },
  transform: {
    description: 'No flow step runs this code.',
    action: 'Use it in a new flow',
  },
};

const COUNTED: Record<TUsedInKind, string> = {
  source: 'counts are runs in the last 24 hours',
  destination: 'counts are copies through the step in the last 24 hours',
  transform: 'counts are copies through the step in the last 24 hours',
};

interface IUsedInFlowsProps {
  kind: TUsedInKind;
  id: string;
  /** The thing's own name: a transform row names the version it pins. */
  name: string;
  timeZone?: string;
  className?: string;
  /** A Library prompt is not "code": its page says what an empty list means for it. */
  emptyText?: string;
}

// Board 17: which flows a source starts, a destination receives from, or a code library runs in.
export const UsedInFlows = ({
  kind,
  id,
  name,
  timeZone,
  className,
  emptyText,
}: IUsedInFlowsProps) => {
  const navigate = useNavigate();
  const [find, setFind] = useState('');
  const [all, setAll] = useState(false);
  const usedIn = useQuery({
    queryKey: usedInKeys.one(kind, id),
    queryFn: () => listUsedIn(kind, id),
  });

  const rows = usedIn.data?.data ?? [];
  const matching = useMemo(() => {
    const needle = find.trim().toLowerCase();
    return needle ? rows.filter((row) => row.flowName.toLowerCase().includes(needle)) : rows;
  }, [rows, find]);
  const shown = all || find.trim() ? matching : matching.slice(0, SHOWN);
  const hidden = matching.length - shown.length;

  // Failing is what the step does now; an issue stays open a while after it recovers.
  const failing = rows.filter((row) => row.failingNow === true);
  const drafted = rows.length > 0 && rows.every((row) => row.onlyInDraft);
  const issueRow = rows.find((row) => row.issueId !== null) ?? null;

  const create = (
    <FlowCreate
      preset={
        kind === 'source'
          ? { sourceId: id }
          : kind === 'destination'
            ? { destinationId: id }
            : undefined
      }
      onCreated={(flow) => void navigate({ to: '/flows/$flowId', params: { flowId: flow.id } })}
      renderTrigger={(openAt) => (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => openAt(kind === 'transform' ? 'scratch' : 'one-step')}
        >
          {kind === 'source' && <PlusIcon size={12} />}
          {EMPTY[kind].action}
        </Button>
      )}
    />
  );

  return (
    <section className={cn('overflow-hidden rounded-panel border border-border-sunken', className)}>
      <header className="flex min-h-secthead items-center gap-inset border-b border-border-sunken px-input py-2">
        <h2 className="text-data font-semibold text-primary">Used in flows</h2>
        {usedIn.data && (
          <span className="font-mono text-micro text-faint">
            {usedIn.data.total.toLocaleString('en-US')}
            {drafted ? ', in a draft' : ''}
          </span>
        )}
        <span className="ml-auto flex items-center gap-2">
          {failing.length > 0 && (
            <span className="flex items-center gap-2 text-meta text-primary">
              <span aria-hidden className="size-dot rounded-full bg-danger" />
              {failing.length.toLocaleString('en-US')} failing now
            </span>
          )}
          {rows.length > SHOWN && (
            <Input
              size="sm"
              aria-label="Find a flow"
              placeholder="Find"
              leading={<SearchIcon size={12} />}
              value={find}
              onChange={(event) => setFind(event.target.value)}
            />
          )}
          {kind === 'source' && rows.length > 0 && rows.length <= SHOWN && create}
        </span>
      </header>

      {usedIn.isError && (
        <div className="p-input">
          <ErrorState error={usedIn.error} onRetry={() => void usedIn.refetch()} />
        </div>
      )}
      {usedIn.isPending && (
        <div className="p-input">
          <LoadingState rows={2} />
        </div>
      )}

      {usedIn.isSuccess && rows.length === 0 && (
        <div className="flex flex-col items-center gap-2 px-input py-5 text-center">
          <p className="text-data text-primary">Nothing uses it yet</p>
          <p className="text-meta text-muted">{emptyText ?? EMPTY[kind].description}</p>
          <div className="mt-2">{create}</div>
        </div>
      )}

      {shown.map((row: TUsedInRow) => (
        <UsedInRow
          key={`${row.flowId}:${row.node?.key ?? ''}:${row.onlyInDraft}`}
          kind={kind}
          name={name}
          row={row}
        />
      ))}

      {usedIn.isSuccess && find.trim() !== '' && matching.length === 0 && (
        <p className="px-input py-3 text-meta text-muted">No flow here is called that.</p>
      )}

      {rows.length > 0 && (
        <footer className="flex items-center gap-inset px-input py-3 font-mono text-micro text-faint">
          {kind === 'destination' && issueRow?.issueId ? (
            <span className="flex min-w-0 items-center gap-2">
              <Link
                to="/issues/$issueId"
                params={{ issueId: issueRow.issueId }}
                className="shrink-0 font-sans text-meta text-primary underline"
              >
                Open the issue
              </Link>
              <span className="truncate" title={issueRow.issueSummary ?? undefined}>
                {issueRow.issueSummary}
                {issueRow.issueSince ? ` since ${clockTime(issueRow.issueSince, timeZone)}` : ''}
              </span>
            </span>
          ) : drafted ? (
            <span className="font-sans text-meta text-muted">
              It runs nowhere yet. It starts once {rows[0].flowName}
              {rows.length > 1 ? ' or another of these flows' : ''} is published with this draft.
            </span>
          ) : (
            <span>{rows.length > SHOWN && !find.trim() ? 'busiest first' : COUNTED[kind]}</span>
          )}
          {hidden > 0 && (
            <button
              type="button"
              className="ml-auto font-sans text-meta text-primary underline"
              onClick={() => setAll(true)}
            >
              {hidden.toLocaleString('en-US')} more
            </button>
          )}
        </footer>
      )}
    </section>
  );
};
